import type { WebMcpValue } from './registration';
import type { Atom, FailureResult, McpTextContent, McpToolResult, Residue, ResidueSpan, StructureSummary, SuccessResult, ToolResult, Truncation } from './types';

export const MAX_RESULT_ITEMS = 200;
export const MAX_SERIALIZED_CHARACTERS = 8_000;
export const EXPAND_RESULT_CACHE_CAPACITY = 3;

export interface ContactResultItem {
    readonly kind: string
    readonly a: readonly Residue[]
    readonly b: readonly Residue[]
}

export interface VisibilityResultItem {
    readonly ref: string
    readonly visible: boolean
}

export interface SecondaryStructureResultItem {
    readonly residue: Residue
    readonly assignment: 'none' | 'turn' | 'helix' | 'sheet'
    readonly source: 'deposited' | 'computed-dssp' | 'computed-zhang-skolnick'
}

export type ScientificResultItem = WebMcpValue | Atom | Residue | StructureSummary | ContactResultItem | SecondaryStructureResultItem | VisibilityResultItem;

type ExpandPageRenderer = (page: ExpandResultPage) => string;

interface CachedExpandResult {
    id: string
    items: readonly ScientificResultItem[]
    renderer?: ExpandPageRenderer
}

interface ExpandCursor {
    id: string
    offset: number
}

export interface BoundedItems<T> {
    items: readonly T[]
    truncation?: Truncation
}

export interface ExpandResultPage {
    items: readonly ScientificResultItem[]
    truncation?: Truncation
    serialized?: string
}

/** Wrap a WebMCP result in the content format required by MCP clients. */
export function toMcpResult<T>(result: ToolResult<T>): McpToolResult {
    const content: McpTextContent = { type: 'text', text: JSON.stringify(result) };
    return { content: [content] };
}

export function success<T>(summary: string, data: T, truncation?: Truncation): SuccessResult<T> {
    if (truncation === void 0) return { ok: true, summary, data };
    return { ok: true, summary, data, truncation };
}

export function failure(error: string, hint: string): FailureResult {
    return { ok: false, error, hint };
}

interface CollectedItems<T> {
    readonly items: readonly T[]
    readonly nextOffset: number
}

/** Serializes each item once instead of reserializing an ever-growing array. */
function collectBoundedItems<T>(items: readonly T[], offset: number): CollectedItems<T> {
    const bounded: T[] = [];
    let serializedLength = 2; // The surrounding JSON array brackets.
    let index = offset;
    while (index < items.length && bounded.length < MAX_RESULT_ITEMS) {
        const itemLength = JSON.stringify([items[index]]).length - 2;
        const candidateLength = serializedLength + (bounded.length === 0 ? 0 : 1) + itemLength;
        if (candidateLength > MAX_SERIALIZED_CHARACTERS && bounded.length !== 0) break;
        // A single oversized item is returned so its cursor always advances.
        bounded.push(items[index]);
        serializedLength = candidateLength;
        index++;
    }
    return { items: bounded, nextOffset: index };
}

/** Collapse adjacent canonical residues without losing an insertion-coded singleton identity. */
export function collapseResidueRanges(residues: readonly Residue[]): readonly ResidueSpan[] {
    const ranges: ResidueSpan[] = [];
    let start: Residue | undefined;
    let previous: Residue | undefined;

    const finishRange = () => {
        if (start === void 0 || previous === void 0) return;
        const range: ResidueSpan = {
            ref: start.ref,
            chainAuth: start.chainAuth,
            chainLabel: start.chainLabel,
            fromSeqAuth: start.seqAuth,
            toSeqAuth: previous.seqAuth,
            fromSeqLabel: start.seqLabel,
            toSeqLabel: previous.seqLabel,
        };
        if (start === previous && start.insCode !== void 0) range.insCode = start.insCode;
        if (start.comp === previous.comp) range.comp = start.comp;
        ranges.push(range);
    };

    for (const residue of residues) {
        const contiguous = previous !== void 0
            && previous.ref === residue.ref
            && previous.chainAuth === residue.chainAuth
            && previous.chainLabel === residue.chainLabel
            && previous.insCode === void 0
            && residue.insCode === void 0
            && previous.seqAuth + 1 === residue.seqAuth
            && previous.seqLabel !== void 0
            && residue.seqLabel !== void 0
            && previous.seqLabel + 1 === residue.seqLabel;
        if (!contiguous) {
            finishRange();
            start = residue;
        }
        previous = residue;
    }
    finishRange();
    return ranges;
}

/**
 * Stores only truncated result sets for one WebMCP behavior instance.
 * Cursor offsets advance on every page and terminal pages remove their cache entry.
 */
export class ExpandResultCache {
    private readonly entries: CachedExpandResult[] = [];
    private nextId = 1;

    create<T extends ScientificResultItem>(items: readonly T[]): BoundedItems<T> {
        const id = `expand-${this.nextId++}`;
        const page = this.page(items, 0, id);
        if (page.truncation !== void 0) this.remember({ id, items });
        return page;
    }

    createFitting<T extends ScientificResultItem>(items: readonly T[], serializedLength: (page: BoundedItems<T>) => number): BoundedItems<T> {
        const id = `expand-${this.nextId++}`;
        const page = this.fittingPage(items, 0, id, serializedLength);
        if (page.truncation !== void 0) this.remember({ id, items });
        return page;
    }

    createRendered<T extends ScientificResultItem>(items: readonly T[], render: (page: BoundedItems<T>) => string): BoundedItems<T> {
        // SAFETY: the cached array is never mutated or mixed with another result, so every expanded item remains T.
        const renderer: ExpandPageRenderer = page => render(page as BoundedItems<T>);
        const id = `expand-${this.nextId++}`;
        const page = this.fittingPage(items, 0, id, candidate => renderer(candidate).length);
        if (page.truncation !== void 0) this.remember({ id, items, renderer });
        return page;
    }

    expand(cursor: string): ExpandResultPage | undefined {
        const requested = parseCursor(cursor);
        if (requested === void 0) return void 0;
        const index = this.entries.findIndex(entry => entry.id === requested.id);
        if (index < 0) return void 0;
        const entry = this.entries[index];
        if (requested.offset >= entry.items.length) return void 0;
        this.entries.splice(index, 1);
        const page = this.fittingPage(entry.items, requested.offset, entry.id, candidate => entry.renderer
            ? entry.renderer(candidate).length
            : JSON.stringify(success('Expanded result returned.', { type: 'expanded-result', items: candidate.items }, candidate.truncation)).length);
        if (page.truncation?.cursor !== void 0) this.entries.unshift(entry);
        return entry.renderer ? { ...page, serialized: entry.renderer(page) } : page;
    }

    hasResults(): boolean { return this.entries.length !== 0; }

    clear() { this.entries.length = 0; }

    private page<T extends ScientificResultItem>(items: readonly T[], offset: number, id: string): BoundedItems<T> {
        const page = collectBoundedItems(items, offset);
        if (page.nextOffset === items.length) return offset === 0 ? { items: page.items } : { items: page.items, truncation: { total: items.length, offset, returned: page.items.length } };
        return {
            items: page.items,
            truncation: { total: items.length, offset, returned: page.items.length, cursor: formatCursor(id, page.nextOffset) },
        };
    }

    private fittingPage<T extends ScientificResultItem>(items: readonly T[], offset: number, id: string, serializedLength: (page: BoundedItems<T>) => number): BoundedItems<T> {
        const available = Math.min(MAX_RESULT_ITEMS, items.length - offset);
        let low = 0;
        let high = available;
        let count = 0;
        while (low <= high) {
            const candidateCount = Math.floor((low + high) / 2);
            const nextOffset = offset + candidateCount;
            const candidate: BoundedItems<T> = nextOffset === items.length
                ? offset === 0 ? { items: items.slice(offset, nextOffset) } : { items: items.slice(offset, nextOffset), truncation: { total: items.length, offset, returned: candidateCount } }
                : { items: items.slice(offset, nextOffset), truncation: { total: items.length, offset, returned: candidateCount, cursor: formatCursor(id, nextOffset) } };
            if (serializedLength(candidate) <= MAX_SERIALIZED_CHARACTERS) {
                count = candidateCount;
                low = candidateCount + 1;
            } else {
                high = candidateCount - 1;
            }
        }
        if (count === 0 && available > 0) count = 1;
        const nextOffset = offset + count;
        if (nextOffset === items.length) return offset === 0 ? { items: items.slice(offset, nextOffset) } : { items: items.slice(offset, nextOffset), truncation: { total: items.length, offset, returned: count } };
        return { items: items.slice(offset, nextOffset), truncation: { total: items.length, offset, returned: count, cursor: formatCursor(id, nextOffset) } };
    }

    private remember(entry: CachedExpandResult) {
        this.entries.unshift(entry);
        if (this.entries.length > EXPAND_RESULT_CACHE_CAPACITY) this.entries.pop();
    }
}

export function pagedResult<T extends ScientificResultItem, D>(cache: ExpandResultCache, items: readonly T[], build: (page: BoundedItems<T>) => ToolResult<D>): McpToolResult {
    const page = cache.createRendered(items, candidate => JSON.stringify(build(candidate)));
    return toMcpResult(build(page));
}

function formatCursor(id: string, offset: number): string {
    return `${id}:${offset}`;
}

function parseCursor(cursor: string): ExpandCursor | undefined {
    const separator = cursor.lastIndexOf(':');
    if (separator < 1) return void 0;
    const id = cursor.slice(0, separator);
    const offset = Number(cursor.slice(separator + 1));
    if (!Number.isSafeInteger(offset) || offset < 0) return void 0;
    return { id, offset };
}
